import React, { useEffect, useState } from "react";
import {
  agenciesFormMapDispatchToProps,
  agenciesFormMapStateToProps,
  agenciesFormReducer,
} from "./AgenciesForm.state";
import { useHistory, useRouteMatch } from "react-router-dom";
import {
  AgencyResponse,
  ErrorWithMessage,
  FormType,
  MatchOnRouteId,
  Pages,
  ReduxFreeformActions,
  ReduxStoreRoot,
} from "../../types";
import { useClearFormOnUnmount, useSetFormFieldsState } from "../../hooks";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchFunction,
  formSelectToSearchableSelectOptions,
  getErrorDictionary,
  handleSelect,
  handleSelectAll,
} from "../../util";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ButtonWithAction,
  FormInput,
  Loading,
  RadioGroup,
  Text,
  Title,
  constants,
  SearchableSelect,
  FieldActions,
  SearchableSelectOption,
  Cluster,
  Box,
  Table,
  TableBody,
  TableHead,
  TableHeading,
  TableRow,
  TableCell,
  Alert,
} from "@thecb/components";
import { FormContainer, Navigation } from "../..";
import { ALERTS, CREATE, SELECT_ALL } from "../../util/constants";
import { RequestMethod } from "../../types/api/RequestMethod";
import { replacePOSAlerts } from "../../state/root";
import { AlertVariants } from "../../types/Alert";
import Agency from "../../types/api/models/agency";
interface Props {
  actions: {
    forms: {
      agenciesForm: {
        actions: ReduxFreeformActions;
      };
    };
  };
}
const AgenciesForm = ({ actions }: Props) => {
  const formActions = actions.forms.agenciesForm.actions;
  useClearFormOnUnmount(formActions.form);
  const dispatch = useDispatch();
  const history = useHistory();
  const [formType, setFormType] = useState<FormType>(FormType.EDIT);
  const { agenciesForm } = useSelector(
    (state: ReduxStoreRoot) => state.posConfigTool.agencies.forms
  );

  const isSplitFeeDisabled = Boolean(
    agenciesForm.convenience_fee.rawValue ||
      agenciesForm.max_card_amount_allowed.rawValue
  );

  const isConvenienceFeeDisabled = Boolean(
    agenciesForm.split_fee_mid.rawValue ||
      agenciesForm.split_fee_text.rawValue ||
      agenciesForm.service_fee_percentage.rawValue ||
      agenciesForm.minimum_service_fee.rawValue
  );

  const { posApiEndpoint } = useSelector((state: ReduxStoreRoot) => state.config);
  const {
    client: {
      value: { brand: selectedBrand },
    },
  } = useSelector((state: ReduxStoreRoot) => state.currentUser);
  const match: MatchOnRouteId | null = useRouteMatch("/pos-config/agencies/:id");
  const agencyId = match?.params?.id;
  const [allLocations, setAllLocations] = useState<SearchableSelectOption[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<
    SearchableSelectOption[]
  >([]);
  const { data: editAgencyData, isFetching: isFetchingView } = useQuery<
    AgencyResponse,
    ErrorWithMessage
  >({
    queryKey: [`GET -> /agencies/${agencyId}`],
    queryFn: () => fetchFunction(`${posApiEndpoint}/agencies/${agencyId}/edit`),
    onSuccess(res) {
      const allLocationsFormatted = formSelectToSearchableSelectOptions(
        res.data.locations
      );
      setAllLocations(allLocationsFormatted);
      const selectedLocationOptions = allLocationsFormatted.filter((location) =>
        res.data.location_ids.includes(location.value ?? "")
      );
      if (selectedLocationOptions.length === res.data.locations.length) {
        selectedLocationOptions.push({
          name: SELECT_ALL,
          value: SELECT_ALL,
        });
      }
      setSelectedLocations(selectedLocationOptions);
    },
    enabled: agencyId !== FormType.CREATE,
  });

  const { data: createAgencyData, isFetching: isFetchingCreate } = useQuery<
    AgencyResponse,
    ErrorWithMessage
  >({
    queryKey: [`GET -> /agencies/${selectedBrand}/new`],
    queryFn: () =>
      fetchFunction(`${posApiEndpoint}/agencies/new?brand=${selectedBrand}`),
    onSuccess(res) {
      const allLocationsFormatted = formSelectToSearchableSelectOptions(
        res.data.locations
      );
      setAllLocations(allLocationsFormatted);
    },
    enabled: formType === FormType.CREATE,
  });

  const { mutate: createAgency, isLoading: isLoadingPOST } = useMutation<
    AgencyResponse,
    ErrorWithMessage,
    { agency: Partial<Agency> }
  >({
    mutationFn: (body: { agency: Partial<Agency> }) =>
      fetchFunction(`${posApiEndpoint}/agencies`, {
        method: RequestMethod.POST,
        body,
      }),
    onSuccess: (res: AgencyResponse) => {
      const newAgencyLink = history.location.pathname.replace(
        CREATE,
        `${res.data.id}/edit`
      );
      history.push(newAgencyLink);
      setFormType(FormType.EDIT);
      dispatch(
        replacePOSAlerts({
          variant: AlertVariants.SUCCESS,
          heading: ALERTS.SUCCESS,
          text: `${res.data.description} Successfully Created`,
          alertClearTimeout: 3000,
        })
      );
    },
  });

  useEffect(() => {
    if (agencyId === FormType.CREATE) {
      setFormType(FormType.CREATE);
    }
  }, [agencyId]);

  const { mutate: updateAgency, isLoading: isLoadingPUT } = useMutation<
    AgencyResponse,
    ErrorWithMessage,
    { agency: Partial<Agency> }
  >({
    mutationFn: (body: { agency: Partial<Agency> }) =>
      fetchFunction(`${posApiEndpoint}/agencies/${body.agency.id}`, {
        method: RequestMethod.PUT,
        body,
      }),
    onSuccess: (res: AgencyResponse) =>
      dispatch(
        replacePOSAlerts({
          variant: AlertVariants.SUCCESS,
          heading: ALERTS.SUCCESS,
          text: `${res.data.description} Successfully Updated`,
          alertClearTimeout: 3000,
        })
      ),
  });

  useSetFormFieldsState(formActions, editAgencyData);
  useSetFormFieldsState(formActions, createAgencyData);

  if (editAgencyData || formType === FormType.CREATE) {
    return (
      <>
        <Title as="h1" weight="bold">
          Agencies
        </Title>
        <Navigation page={Pages.AGENCIES} />
        {formType === FormType.CREATE ? (
          <Title as="h1" weight="bold" extraStyles={`margin: 0.5rem 0`}>
            Create Agency
          </Title>
        ) : (
          ""
        )}
        <FormContainer header={editAgencyData?.data?.description ?? ""}>
          <Title
            as="h2"
            variant="small"
            weight="600"
            extraStyles={`margin-bottom: 0.5rem`}
          >
            Agency Settings
          </Title>
          <FormInput
            errorMessages={getErrorDictionary("Code")}
            fieldActions={formActions.fields.code}
            field={agenciesForm.code}
            labelTextWhenNoError="Code"
          />
          <FormInput
            errorMessages={getErrorDictionary("Slug")}
            fieldActions={formActions.fields.slug}
            field={agenciesForm.slug}
            labelTextWhenNoError="Slug"
          />
          <FormInput
            errorMessages={getErrorDictionary("Description")}
            fieldActions={formActions.fields.description}
            field={agenciesForm.description}
            labelTextWhenNoError="Description"
          />
          {agenciesForm.split_tender.rawValue && (
            <RadioGroup
              field={agenciesForm.split_tender}
              fieldActions={formActions.fields.split_tender}
              groupName="splitTenderAllowed"
              headingText="Split tender allowed"
              config={[
                { id: "Yes", value: "true", labelText: "Yes" },
                { id: "No", value: "false", labelText: "No" },
              ]}
            />
          )}
          <>
            <Title as="h2" variant="small" weight="600" margin="16px 0px 0px">
              Location
            </Title>
            <Text color={constants.colors.CHARADE_GREY}>
              Attach locations to this agency
            </Text>
            <SearchableSelect
              actions={formActions as FieldActions}
              fields={agenciesForm}
              items={[{ name: SELECT_ALL, value: SELECT_ALL }, ...allLocations]}
              selectedItems={selectedLocations}
              allSelected={
                selectedLocations?.length === allLocations.length + 1 ?? false
              }
              toggleSelectAllItems={(isSelectAll) =>
                handleSelectAll(isSelectAll, allLocations, setSelectedLocations)
              }
              selectItem={(selection) =>
                handleSelect(selection, selectedLocations, setSelectedLocations)
              }
              disabled={false}
              placeholder="Select Locations"
            />
          </>
          <Title as="h2" variant="small" weight="600" margin="16px 0px">
            Receipts
          </Title>
          <FormInput
            errorMessages={getErrorDictionary("Receipt Footer")}
            fieldActions={formActions.fields.pay_at_message}
            field={agenciesForm.pay_at_message}
            labelTextWhenNoError="Receipt Footer"
          />
          <FormInput
            errorMessages={getErrorDictionary("Header URL")}
            fieldActions={formActions.fields.header_url}
            field={agenciesForm.header_url}
            labelTextWhenNoError="Header URL"
          />
          <>
            {formType === FormType.EDIT && editAgencyData && (
              <>
                <Title as="h2" variant="small" weight="600" margin="16px 0px">
                  Agency Fee Config (DC Direct PinPads)
                </Title>
                {isSplitFeeDisabled && (
                  <Alert
                    text="Split Fee fields are disabled because Convenience Fee fields have values. Clear the Convenience Fee fields to enable these."
                    extraStyles="margin-top: 16px; margin-bottom: 16px;"
                    showQuitLink={false}
                  />
                )}
                <FormInput
                  errorMessages={getErrorDictionary("Split Fee MID")}
                  fieldActions={formActions.fields.split_fee_mid}
                  field={agenciesForm.split_fee_mid}
                  labelTextWhenNoError="Split Fee MID"
                  disabled={isSplitFeeDisabled}
                  extraStyles={`${isSplitFeeDisabled ? `cursor: not-allowed;` : ""}`}
                />
                <FormInput
                  errorMessages={getErrorDictionary("Split Fee Text")}
                  fieldActions={formActions.fields.split_fee_text}
                  field={agenciesForm.split_fee_text}
                  labelTextWhenNoError="Split Fee Text"
                  placeholder={
                    !isSplitFeeDisabled
                      ? "REGULAR AMOUNT: $#PAYMENT##N#MVP FEE: $#FEE1#"
                      : ""
                  }
                  disabled={isSplitFeeDisabled}
                  extraStyles={`${isSplitFeeDisabled ? `cursor: not-allowed;` : ""}`}
                />
                <Text as="p">
                  The following variables can be used to configure a Split Fee Text:
                </Text>
                <Box
                  padding="10"
                  width="auto"
                  boxShadow="0px 0px 5px rgba(202, 206, 216, 1)"
                  borderRadius="4px"
                >
                  <Table extraStyles={"margin-bottom: 15px"}>
                    <TableHead>
                      <TableHeading>Variable</TableHeading>
                      <TableHeading>Description</TableHeading>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>$#PAYMENT#</TableCell>
                        <TableCell>
                          The amount of the payment (Purchase + Gratuity + Cash Back)
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>#N#</TableCell>
                        <TableCell>A new line</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>$#FEE1#</TableCell>
                        <TableCell>The amount of SplitFee1</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
                <Title as="h2" variant="small" weight="600" margin="16px 0px">
                  Service Fee Settings
                </Title>
                <FormInput
                  errorMessages={getErrorDictionary("Service Fee")}
                  fieldActions={formActions.fields.service_fee_percentage}
                  field={agenciesForm.service_fee_percentage}
                  labelTextWhenNoError="Service Fee (%)"
                  decorator="Percentage of the total amount charged as a service fee."
                  disabled={isSplitFeeDisabled}
                />
                <FormInput
                  errorMessages={getErrorDictionary("Minimum Service Fee")}
                  fieldActions={formActions.fields.minimum_service_fee}
                  field={agenciesForm.minimum_service_fee}
                  labelTextWhenNoError="Minimum Service Fee (in $)"
                  decorator="Minimum fee applied if the calculated percentage is too low."
                  disabled={isSplitFeeDisabled}
                />
                <Title as="h2" variant="small" weight="600" margin="16px 0px">
                  Convenience Fee Settings
                </Title>
                {isConvenienceFeeDisabled && (
                  <Alert
                    text="Convenience Fee fields are disabled because Split Fee or Service Fee fields have values. Clear the Split Fee fields to enable these."
                    extraStyles="margin-top: 16px; margin-bottom: 16px; auto;"
                    showQuitLink={false}
                  />
                )}
                <FormInput
                  errorMessages={getErrorDictionary("Convenience Fee")}
                  fieldActions={formActions.fields.convenience_fee}
                  field={agenciesForm.convenience_fee}
                  labelTextWhenNoError="Convenience Fee"
                  placeholder={!isConvenienceFeeDisabled ? "1.50" : ""}
                  disabled={isConvenienceFeeDisabled}
                  extraStyles={`${
                    isConvenienceFeeDisabled ? `cursor: not-allowed;` : ""
                  }`}
                />
                <FormInput
                  errorMessages={getErrorDictionary("Max Card Amount Allowed")}
                  fieldActions={formActions.fields.max_card_amount_allowed}
                  field={agenciesForm.max_card_amount_allowed}
                  labelTextWhenNoError="Max Card Amount Allowed"
                  placeholder={!isConvenienceFeeDisabled ? "99.50" : ""}
                  disabled={isConvenienceFeeDisabled}
                  extraStyles={`${
                    isConvenienceFeeDisabled ? `cursor: not-allowed;` : ""
                  }`}
                />
              </>
            )}
          </>
          <Cluster justify="flex-end">
            <ButtonWithAction
              disabled={Object.values(agenciesForm).some(
                (formValue) => formValue.hasErrors
              )}
              extraStyles="margin: 0;"
              text={formType === FormType.CREATE ? "Create Agency" : "Save Changes"}
              action={(e) => {
                e.preventDefault();
                if (formType === FormType.EDIT && editAgencyData) {
                  updateAgency({
                    agency: {
                      id: editAgencyData.data.id,
                      code: agenciesForm.code.rawValue ?? "",
                      slug: agenciesForm.slug.rawValue ?? "",
                      description: agenciesForm.description.rawValue ?? "",
                      split_tender: agenciesForm.split_tender.rawValue === "true",
                      location_ids: selectedLocations
                        .filter((location) => location.name !== SELECT_ALL)
                        .map((location) => Number(location.value)),
                      receipt_attributes: {
                        header_url: agenciesForm.header_url.rawValue ?? "",
                        pay_at_message: agenciesForm.pay_at_message.rawValue ?? "",
                      },
                      agency_fee_config: {
                        split_fee_mid: agenciesForm.split_fee_mid.rawValue ?? "",
                        split_fee_text: agenciesForm.split_fee_text.rawValue ?? "",
                        convenience_fee: parseFloat(
                          agenciesForm.convenience_fee.rawValue ?? ""
                        ),
                        max_card_amount_allowed: parseFloat(
                          agenciesForm.max_card_amount_allowed.rawValue ?? ""
                        ),
                        service_fee_percentage:
                          agenciesForm.service_fee_percentage.rawValue ?? "",
                        minimum_service_fee:
                          agenciesForm.minimum_service_fee.rawValue ?? "",
                      },
                    },
                  });
                }
                if (formType === FormType.CREATE && createAgencyData) {
                  createAgency({
                    agency: {
                      client_id: createAgencyData.data.client_id,
                      code: agenciesForm.code.rawValue ?? "",
                      slug: agenciesForm.slug.rawValue ?? "",
                      description: agenciesForm.description.rawValue ?? "",
                      split_tender: agenciesForm.split_tender.rawValue === "true",
                      location_ids: selectedLocations
                        .filter((location) => location.name !== SELECT_ALL)
                        .map((location) => Number(location.value)),
                      receipt_attributes: {
                        header_url: agenciesForm.header_url.rawValue ?? "",
                        pay_at_message: agenciesForm.pay_at_message.rawValue ?? "",
                      },
                    },
                  });
                }
              }}
            />
          </Cluster>
        </FormContainer>
      </>
    );
  }

  if (isFetchingView || isLoadingPUT || isFetchingCreate || isLoadingPOST) {
    return <Loading />;
  }

  return null;
};

AgenciesForm.reducer = agenciesFormReducer;
AgenciesForm.mapStateToProps = agenciesFormMapStateToProps;
AgenciesForm.mapDispatchToProps = agenciesFormMapDispatchToProps;

export default AgenciesForm;
